import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { api } from '../../utils/api';
import type { AwDocOffer, AwRelatedDoc, AwRelatedDocs, AwRelatedRef } from '../../types';
import type { AwCtx } from './ctx';
import { WORKSPACE_LAYER } from '../MerchantDoc/shell';
import { placeRowMenu, swallowDismissingClick, type RowMenuPlace } from './RowMenu';
import { SourceDocDialog, opensAWindow } from './sourceDoc';
import { PoViewDialog } from './panes/Purchases';
import { ClaimWindow } from './panes/Expenses';
import { EntryDetailDialog } from './JournalBook';
import { EvidenceWindow } from './attachments';
import { TAP_TARGET, salesStatus, salesStatusKey } from './listCells';
import { amt, dmy, pillAmber, pillGreen, pillGrey, pillRed } from './ui';

// ── A document's related documents: an icon beside its number (owner 2026-11-02, "Linked Document Hover Preview") ─────
// The icon stands only where the list's own query counted at least one (`linked_count`, services/recordLinks.ts), so it follows a
// conversion or a void the next time the list is read, with nothing to keep up to date. The rows are read as the panel opens, from
// GET /acct-workspace/related/:kind/:id: the same walk, always a 200.
//
// ⛔ HOVER, CLICK AND TAP OPEN THE SAME PANEL. A mouse opens it by resting (OPEN_MS), so passing over a column of icons reads nothing;
// leaving closes it after CLOSE_MS, which is what carries the cursor across the gap into the panel, and coming back cancels that. A
// click pins it open until an outside press or Esc. A tap arrives with an emulated mouseenter in front of its click (TOUCH_MS): acted
// on, the hover would open the panel and the click would then toggle it shut, so that mouseenter is ignored and the tap is the click.
//
// ⛔ PORTALLED AND FIXED, off the icon's own rect, as RowMenu.tsx is and for its reasons: the lists sit in a card with `overflowX: auto`,
// and a panel pinned to a rect must not linger where the icon no longer is, so a scroll anywhere else, or a resize, closes it. React
// events still bubble through a portal, so the wrapper stops them: nothing in here is ever the row's click, and nothing navigates.
const OPEN_MS = 120;
const CLOSE_MS = 200;
const TOUCH_MS = 800;

/** A document's type, in words. ⛔ Every key written in full, so a grep finds each; a kind with no word prints nothing. */
const TYPE_KEY: Record<string, string> = {
  quotation: 'aw_rel_kind_quotation', invoice: 'aw_dt_invoice', proforma: 'aw_dt_proforma', tax_invoice: 'aw_dt_tax_invoice', tax_invoice_receipt: 'aw_dt_tax_invoice_receipt',
  credit_note: 'aw_dt_credit_note', debit_note: 'aw_dt_debit_note', receipt: 'aw_jl_src_receipt',
  purchase_order: 'aw_rel_kind_purchase_order', payable: 'aw_jl_src_payable', expense_upload: 'aw_jl_src_expense_upload', purchase_note: 'aw_jl_src_purchase_note',
  expense_payment: 'aw_rel_kind_payment', payable_payment: 'aw_rel_kind_payment',
  // What the Linked Records section lists beside the walk (owner 2026-11-02, services/recordAttachments.ts). Never a list's popover: the walk does not reach them.
  journal_entry: 'aw_rel_kind_entry', bank_line: 'aw_rel_kind_bank_line', cash_row: 'aw_rel_kind_cash_row', reimbursement: 'aw_rel_kind_claim',
};
export const typeWord = (t: TFunction<'translation', undefined>, kind: string, noteKind = ''): string =>
  noteKind === 'credit' ? t('aw_note_kind_credit') : noteKind === 'debit' ? t('aw_note_kind_debit') : TYPE_KEY[kind] ? t(TYPE_KEY[kind]) : '';

/**
 * A status, in words. ⛔ A STORED VALUE PRINTS ITSELF WHEN THERE IS NO WORD FOR IT, sourceDoc.tsx's rule: i18next answers an unknown
 * key with the key. The same word means different things by table (a bill's `open` is unpaid), so the map is per table. A sales document
 * reads as it does on its own list and in the client's portal (listCells.tsx salesStatusKey): an issued bill is never "draft" here.
 */
const STATUS_KEY: Record<string, Record<string, string>> = {
  purchase_order: { draft: 'aw_po_status_draft', approved: 'aw_po_status_approved', received: 'aw_po_status_received', billed: 'aw_po_status_billed', cancelled: 'aw_po_status_cancelled', void: 'aw_rel_st_void' },
  payable: { open: 'aw_pay_status_open', partial: 'aw_pay_status_partial', paid: 'aw_pay_status_paid', void: 'aw_rel_st_void' },
  expense_upload: { pending: 'aw_exp_pending', approved: 'aw_exp_approved', rejected: 'aw_exp_rejected', cancelled: 'aw_er_chip_cancelled', void: 'aw_rel_st_void' },
  purchase_note: { booked: 'aw_pn_chip_booked', not_booked: 'aw_pn_chip_not_booked', void: 'aw_rel_st_void' },
  expense_payment: { live: 'aw_rel_st_recorded', reversed: 'aw_rel_st_reversed' },
  payable_payment: { live: 'aw_rel_st_recorded', reversed: 'aw_rel_st_reversed' },
  journal_entry: { draft: 'aw_jl_status_draft', posted: 'aw_jl_status_posted', void: 'aw_jl_status_void' },
  reimbursement: { submitted: 'aw_rb_status_submitted', approved: 'aw_rb_status_approved', rejected: 'aw_rb_status_rejected', paid: 'aw_rb_status_paid' },
};
const statusTone = (d: AwRelatedDoc): React.CSSProperties => (d.voided || d.status === 'rejected' ? pillRed
  : /paid|approved|received|billed|booked/.test(d.status) && d.status !== 'not_booked' ? pillGreen : /partial|pending/.test(d.status) ? pillAmber : pillGrey);

/** The windows a record can open in, on the page it was asked from. */
export const hasRecordWindow = (kind: string): boolean => kind === 'purchase_order' || kind === 'journal_entry' || kind === 'reimbursement' || kind === 'bank_line' || kind === 'cash_row' || opensAWindow(kind);

/**
 * One record's own window. ⛔ THE WINDOWS THERE ALREADY ARE, each read-only: the document window, the bill, the scan, the supplier's
 * note (sourceDoc.tsx SourceDocDialog) and the order (panes/Purchases.tsx PoViewDialog). Mounted over the page; the URL does not move,
 * so Esc is the way back. A kind with none is never offered (hasRecordWindow).
 *
 * ⛔ PORTALLED TO THE BODY, where every other window here hangs off its pane: this one is held by a table cell (see RelatedDocsBtn), and
 * a Dialog is not portalled itself, so it would inherit the cell's nowrap and a voided row's strike-through.
 */
export function RecordWindow({ ctx, rec, onClose }: { ctx: AwCtx; rec: AwRelatedRef; onClose: () => void }) {
  return createPortal(
    rec.kind === 'purchase_order'
      ? <PoViewDialog ctx={ctx} id={rec.id} onClose={onClose} />
      : rec.kind === 'journal_entry' ? <EntryDetailDialog entryId={rec.id} onClose={onClose} />
      : rec.kind === 'reimbursement' ? <ClaimWindow ctx={ctx} id={rec.id} onClose={onClose} />
      // A bank line or a cash row has no document screen (sourceDoc.tsx's ruling stands, and this is not one): the paperclip's window, with its files and what it is linked to.
      : rec.kind === 'bank_line' || rec.kind === 'cash_row' ? <EvidenceWindow clientId={ctx.clientId} kind={rec.kind} id={rec.id} docNo={rec.docNo} onClose={onClose} />
      : <SourceDocDialog clientId={ctx.clientId} entries onClose={onClose}
          s={{ kind: rec.kind, id: rec.id, docNo: rec.docNo, openable: true, why: '', voided: !!rec.voided, ...(rec.record ? { record: rec.record } : {}) }} />,
    document.body,
  );
}

/** openRecord(kind, id) and the window it opens, for a host to mount wherever it lives: what the popover uses and all it needs. */
export function useOpenRecord(ctx: AwCtx): { openRecord: (kind: string, id: string, docNo?: string, record?: AwRelatedRef['record'], voided?: boolean) => void; recordWindow: React.ReactNode } {
  const [rec, setRec] = useState<AwRelatedRef | null>(null);
  return {
    openRecord: (kind, id, docNo = '', record = null, voided = false) => setRec({ kind, id, docNo, record, voided }),
    recordWindow: rec && <RecordWindow ctx={ctx} rec={rec} onClose={() => setRec(null)} />,
  };
}

/** One line of the panel. Every number opens its document, struck where the row is voided (the requirement's sections 2 and 3); only a row
 *  no window can read (recordLinks.ts `openable`), or a kind with no window, prints as plain text, and says why on hover. */
export function RelatedRow({ d, onOpen }: { d: AwRelatedDoc; onOpen: (r: AwRelatedRef) => void }) {
  const { t } = useTranslation();
  const target: AwRelatedRef = d.opens || d;
  const canOpen = d.openable && hasRecordWindow(target.kind);
  const no = d.docNo || t('aw_rel_no_number');
  const statusKey = salesStatusKey(d.kind, d.status) || STATUS_KEY[d.kind]?.[d.status];
  const payment = d.kind === 'expense_payment' || d.kind === 'payable_payment';
  return (
    <div data-related-row={d.kind} data-related-voided={d.voided ? '1' : undefined} style={{ padding: '6px 8px', borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 8, fontSize: 12.5 }}>
        <span style={{ color: 'var(--text-secondary)' }}>{typeWord(t, d.kind, d.noteKind)}</span>
        {canOpen
          ? (
            <button type="button" data-related-open={d.id} onClick={() => onOpen(target)} title={t(payment ? 'aw_rel_payment_title' : 'aw_rel_open_title')}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--accent)', fontSize: 'inherit', fontVariantNumeric: 'tabular-nums', ...(d.voided ? { textDecoration: 'line-through' } : {}) }}>
              {no}
            </button>
          ) : (
            <span data-related-text={d.id} title={t(d.voided ? 'aw_rel_voided_title' : 'aw_rel_no_window')}
              style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--text-tertiary)', ...(d.voided ? { textDecoration: 'line-through' } : {}) }}>
              {no}
            </span>
          )}
        {!!d.subNo && <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{d.subNo}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 8, rowGap: 2, marginTop: 2, fontSize: 11.5, color: 'var(--text-secondary)' }}>
        {!!d.date && <span>{dmy(d.date)}</span>}
        {!!d.status && <span style={salesStatusKey(d.kind, d.status) ? salesStatus(t, d.kind, d.status).tone : statusTone(d)}>{statusKey ? t(statusKey) : d.status}</span>}
        {d.total !== null && <span style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(d.total)} {d.currency}</span>}
      </div>
    </div>
  );
}

/** The icon and its panel, once a list has counted something (below); or, given `dot`, the dot before a quotation's number and the same panel,
 *  which a click opens and a hover does not: a column of dots stands where the mouse travels, and most have nothing to say. */
function RelatedDocsOpen({ ctx, kind, id, docNo, dot }: { ctx: AwCtx; kind: string; id: string; docNo: string; dot?: { state: string; color: string; title: string } }) {
  const { t } = useTranslation();
  const { openRecord, recordWindow } = useOpenRecord(ctx);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<RowMenuPlace | null>(null);
  const [data, setData] = useState<AwRelatedDocs | null>(null);
  const [err, setErr] = useState('');
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number>(0);
  const touchedAt = useRef(0);
  const byKeyboard = useRef(false);

  const clearTimer = () => { window.clearTimeout(timer.current); timer.current = 0; };
  useEffect(() => clearTimer, []);
  const show = (pin: boolean) => { clearTimer(); setPinned(pin); setData(null); setErr(''); setPos(null); setOpen(true); };
  const close = (refocus: boolean) => { clearTimer(); setOpen(false); setPinned(false); if (refocus) btnRef.current?.focus(); };
  const leaveSoon = () => { if (open && !pinned) { clearTimer(); timer.current = window.setTimeout(() => close(false), CLOSE_MS); } };

  // Read each time it opens: the row's count is as old as the list, and this is what stands now.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    api.awRelatedDocs(ctx.clientId, kind, id)
      .then((r: AwRelatedDocs) => { if (alive) setData(r); })
      .catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [open, ctx.clientId, kind, id]);

  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      const n = e.target as Node;
      if (btnRef.current?.contains(n) || panelRef.current?.contains(n)) return;
      close(false);
      // The press that dismissed a panel somebody opened on purpose goes on to be a click, and the row under it opens on a click.
      if (pinned) swallowDismissingClick();
    };
    // Capture, so Esc answers the panel alone: the window behind it listens on the document too (utils/escLayer.ts).
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(true); } };
    const bail = (e: Event) => { if (!(e.target instanceof Node && panelRef.current?.contains(e.target))) close(false); };
    const gone = () => close(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    document.addEventListener('keydown', esc, true);
    window.addEventListener('scroll', bail, true);
    window.addEventListener('resize', gone);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
      document.removeEventListener('keydown', esc, true);
      window.removeEventListener('scroll', bail, true);
      window.removeEventListener('resize', gone);
    };
  }, [open, pinned]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Measured before paint, and again when the answer changes what the panel holds. Its LEFT edge on the icon's, since the number
  // column is the table's first and a panel right-aligned to it would run off the page: placeRowMenu's own arithmetic otherwise.
  useLayoutEffect(() => {
    if (!open) return;
    const r = btnRef.current?.getBoundingClientRect();
    const p = panelRef.current;
    if (!r || !p) return;
    const box = p.getBoundingClientRect();
    setPos(placeRowMenu({ top: r.top, bottom: r.bottom, right: r.left + box.width }, { width: box.width, height: Math.max(box.height, p.scrollHeight) }, { width: window.innerWidth, height: window.innerHeight }));
  }, [open, data, err]);

  const links = (): HTMLElement[] => Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[data-related-open]') || []);
  // Opened from the keyboard: into the panel, which hangs off <body> and so is nowhere in the page's Tab order. A mouse stays where it is.
  useEffect(() => {
    if (open && pos && data && byKeyboard.current) { byKeyboard.current = false; links()[0]?.focus(); }
  }, [open, pos, data]);
  const keys = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const list = links();
    const at = list.indexOf(document.activeElement as HTMLElement);
    if (!list.length || (e.shiftKey ? at <= 0 : at === list.length - 1)) { e.preventDefault(); close(true); }
  };
  const name = t('aw_rel_title');
  const selfType = data?.self ? typeWord(t, data.self.kind) : '';
  const selfNo = data?.self?.docNo || docNo;
  // A dot stands in front of the number, whose span is how a row's number is read (`[data-no-cell] > span`): it must not be the first one in the cell.
  const Root: React.ElementType = dot ? 'div' : 'span';

  return (
    // ⛔ Everything below hangs off this element in React's tree, portals included, so a click anywhere in it stops here.
    <Root onClick={(e: React.MouseEvent) => e.stopPropagation()} style={{ display: 'inline-flex', verticalAlign: 'baseline' }}>
      {/* An empty title, not none: with none the browser falls back to the nearest ancestor's, a row's "Open this … in full screen". */}
      <button ref={btnRef} type="button" className={TAP_TARGET} {...(dot ? { 'data-related-dot': dot.state } : { 'data-related-docs': id })} title={open ? '' : dot?.title || name} aria-label={dot?.title || name} aria-haspopup="dialog" aria-expanded={open}
        onTouchStart={() => { touchedAt.current = Date.now(); }}
        onMouseEnter={dot ? undefined : () => {
          if (Date.now() - touchedAt.current < TOUCH_MS) return;
          clearTimer();
          if (!open) timer.current = window.setTimeout(() => show(false), OPEN_MS);
        }}
        onMouseLeave={dot ? undefined : () => { if (open) leaveSoon(); else clearTimer(); }}
        onClick={e => {
          // A click is what a keyboard's Enter is too, and only that one has no pointer behind it.
          byKeyboard.current = e.detail === 0;
          if (open && pinned) close(false);
          else if (open) { clearTimer(); setPinned(true); }
          else show(true);
        }}
        style={dot
          ? { background: 'none', border: 'none', padding: '3px 6px 3px 0', cursor: 'pointer', lineHeight: 1, display: 'inline-flex', alignItems: 'center' }
          : { background: 'none', border: 'none', padding: '1px 4px', marginLeft: 3, cursor: 'pointer', color: open ? 'var(--accent)' : 'var(--text-tertiary)', fontSize: 13, lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}>
        {dot ? <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: dot.color, display: 'inline-block' }} /> : <i className="ti ti-file-text" aria-hidden />}
      </button>
      {open && createPortal(
        <div ref={panelRef} role="dialog" aria-label={name} aria-busy={!data && !err} data-related-panel={id}
          onMouseEnter={clearTimer} onMouseLeave={leaveSoon} onKeyDown={keys}
          // Bubbled in React's tree to the span above, which is why it needs no onClick of its own.
          style={{
            position: 'fixed', top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden', zIndex: WORKSPACE_LAYER + 1,
            // max-content: a fixed box left to shrink-to-fit is as wide as the room right of `left`, so measuring it and placing it would chase each other.
            width: 'max-content', minWidth: 230, maxWidth: Math.min(420, window.innerWidth - 16), maxHeight: pos?.maxHeight, overflowY: 'auto', boxSizing: 'border-box',
            background: 'var(--bg-surface)', border: '1px solid var(--border-strong)', borderRadius: 10, boxShadow: 'var(--shadow-md)', padding: 5,
          }}>
          <div style={{ padding: '5px 8px 6px' }}>
            {!!selfNo && <div data-related-self="" style={{ fontSize: 12, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{selfType ? t('aw_rel_self', { type: selfType, no: selfNo }) : selfNo}</div>}
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', marginTop: 1 }}>{name}</div>
          </div>
          {err && <div style={{ padding: '6px 8px', fontSize: 12, color: '#B42318' }}>{err}</div>}
          {!data && !err && <div style={{ padding: '8px', textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" aria-hidden /></div>}
          {data && !data.docs.length && <div data-related-none="" style={{ padding: '6px 8px', fontSize: 12, color: 'var(--text-tertiary)' }}>{t('aw_rel_none')}</div>}
          {data?.docs.map(d => (
            <RelatedRow key={`${d.kind}:${d.id}`} d={d} onOpen={r => { close(false); openRecord(r.kind, r.id, r.docNo, r.record, r.voided); }} />
          ))}
          {data?.truncated && <div style={{ padding: '6px 8px', fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('aw_rel_truncated', { n: data.docs.length })}</div>}
        </div>,
        document.body,
      )}
      {recordWindow}
    </Root>
  );
}

/**
 * What a list puts after a document's number: nothing at all until its query counted a related document. `kind` is what the list
 * asks /related by: its family key, a stored doc_type, or 'expense' with the register's key as the id.
 */
export default function RelatedDocsBtn({ ctx, kind, id, docNo, count }: { ctx: AwCtx; kind: string; id: string; docNo: string; count?: number }) {
  return count ? <RelatedDocsOpen ctx={ctx} kind={kind} id={id} docNo={docNo} /> : null;
}

// ── The dot before a quotation's number (owner 2026-11-02, "Quotation Status Management & Document Conversion", section 3) ────────────────
// Red for Rejected, blue when at least one live document follows it, grey otherwise, Rejected winning: what the server said of this row (AwDoc.offer,
// services/quotationStage.ts), never worked out here. So a proforma, or a document since voided, leaves the dot grey with the icon still beside the
// number, which says the chain exists. Every row has one and a click opens the icon's own panel, which says so plainly when there is nothing to list.
const DOT_COLOR: Record<string, string> = { red: '#D92D20', blue: '#2563EB', grey: '#98A2B3' };
const DOT_TITLE: Record<string, string> = { red: 'aw_offer_dot_rejected', blue: 'aw_offer_dot_blue', grey: 'aw_offer_dot_grey' };
export function OfferDot({ ctx, id, docNo, offer, voided }: { ctx: AwCtx; id: string; docNo: string; offer?: AwDocOffer; voided?: boolean }) {
  const { t } = useTranslation();
  const state = voided ? 'grey' : offer?.status === 'rejected' ? 'red' : offer?.docs ? 'blue' : 'grey';
  return <RelatedDocsOpen ctx={ctx} kind="quotation" id={id} docNo={docNo} dot={{ state, color: DOT_COLOR[state], title: t(DOT_TITLE[state]) }} />;
}
